<template>
    <div class="error-boundary">
        <div v-if="hasError" class="error-container">
            <EmptyState :type="errorType" :title="errorTitle" :description="errorDescription" :error-code="errorCode"
                :show-retry="showRetry" :show-contact="showContact" @action="handleRetry" />
        </div>
        <div v-else class="error-boundary-content">
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onErrorCaptured, watch } from 'vue';
import { useMessage, useNotification } from 'naive-ui';
import { $t } from '@/locales';
import EmptyState from './EmptyState.vue';

interface Props {
    fallbackType?: 'error' | 'network-error' | 'server-error' | 'timeout-error' | 'permission-error';
    showNotification?: boolean;
    autoRetry?: boolean;
    maxRetries?: number;
    retryDelay?: number;
}

const props = withDefaults(defineProps<Props>(), {
    fallbackType: 'error',
    showNotification: true,
    autoRetry: false,
    maxRetries: 3,
    retryDelay: 2000
});

const emit = defineEmits<{
    error: [error: Error, errorInfo: any];
    retry: [attempt: number];
    recovered: [];
}>();

// State
const hasError = ref(false);
const error = ref<Error | null>(null);
const errorInfo = ref<any>(null);
const retryCount = ref(0);
const isRetrying = ref(false);

// Services
const message = useMessage();
const notification = useNotification();

// Computed
const errorType = computed(() => {
    if (!error.value) return props.fallbackType;

    const errorMessage = error.value.message.toLowerCase();

    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        return 'network-error';
    }
    if (errorMessage.includes('timeout')) {
        return 'timeout-error';
    }
    if (errorMessage.includes('permission') || errorMessage.includes('unauthorized')) {
        return 'permission-error';
    }
    if (errorMessage.includes('server') || errorMessage.includes('500')) {
        return 'server-error';
    }

    return props.fallbackType;
});

const errorTitle = computed(() => {
    switch (errorType.value) {
        case 'network-error':
            return $t('support.errors.networkError');
        case 'timeout-error':
            return $t('support.errors.timeoutError');
        case 'permission-error':
            return $t('support.errors.permissionError');
        case 'server-error':
            return $t('support.errors.serverError');
        default:
            return $t('support.errors.genericError');
    }
});

const errorDescription = computed(() => {
    if (!error.value) return $t('support.errors.genericErrorDesc');

    switch (errorType.value) {
        case 'network-error':
            return $t('support.errors.networkErrorDesc');
        case 'timeout-error':
            return $t('support.errors.timeoutErrorDesc');
        case 'permission-error':
            return $t('support.errors.permissionErrorDesc');
        case 'server-error':
            return $t('support.errors.serverErrorDesc');
        default:
            return $t('support.errors.genericErrorDesc');
    }
});

const errorCode = computed(() => {
    if (!error.value) return undefined;

    // Extract error code from error object or message
    if ('code' in error.value) {
        return (error.value as any).code;
    }

    // Try to extract HTTP status codes
    const statusMatch = error.value.message.match(/(\d{3})/);
    if (statusMatch) {
        return statusMatch[1];
    }

    return error.value.name;
});

const showRetry = computed(() => {
    return retryCount.value < props.maxRetries && !isRetrying.value;
});

const showContact = computed(() => {
    return errorType.value === 'server-error' || retryCount.value >= props.maxRetries;
});

// Error capture
onErrorCaptured((err: Error, instance, info) => {
    console.error('Error caught by ErrorBoundary:', err, info);

    handleError(err, info);

    // Emit error event
    emit('error', err, info);

    // Show notification if enabled
    if (props.showNotification) {
        showErrorNotification(err);
    }

    // Auto retry if enabled
    if (props.autoRetry && retryCount.value < props.maxRetries) {
        scheduleAutoRetry();
    }

    // Prevent the error from propagating
    return false;
});

// Methods
const handleError = (err: Error, info?: any) => {
    hasError.value = true;
    error.value = err;
    errorInfo.value = info;

    // Log error details for debugging
    logErrorDetails(err, info);
};

const logErrorDetails = (err: Error, info?: any) => {
    const errorDetails = {
        message: err.message,
        stack: err.stack,
        name: err.name,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        componentInfo: info
    };

    console.group('🚨 Error Boundary - Error Details');
    console.error('Error:', err);
    console.log('Details:', errorDetails);
    console.groupEnd();

    // In production, send to error tracking service
    if (process.env.NODE_ENV === 'production') {
        // Example: Sentry, LogRocket, etc.
        // errorTrackingService.captureException(err, errorDetails);
    }
};

const showErrorNotification = (err: Error) => {
    const isNetworkError = err.message.toLowerCase().includes('network') ||
        err.message.toLowerCase().includes('fetch');

    if (isNetworkError) {
        notification.error({
            title: $t('support.errors.networkError'),
            description: $t('support.errors.networkErrorDesc'),
            duration: 5000
        });
    } else {
        notification.error({
            title: $t('support.errors.genericError'),
            description: $t('support.errors.genericErrorDesc'),
            duration: 3000
        });
    }
};

const handleRetry = async () => {
    if (isRetrying.value || retryCount.value >= props.maxRetries) {
        return;
    }

    isRetrying.value = true;
    retryCount.value++;

    try {
        // Clear error state
        hasError.value = false;
        error.value = null;
        errorInfo.value = null;

        // Emit retry event
        emit('retry', retryCount.value);

        // Add a small delay to prevent rapid retries
        await new Promise(resolve => setTimeout(resolve, 500));

        // Show success message if retry succeeds
        message.success($t('support.messages.retrySuccess'));
        emit('recovered');

    } catch (retryError) {
        console.error('Retry failed:', retryError);
        handleError(retryError as Error);
    } finally {
        isRetrying.value = false;
    }
};

const scheduleAutoRetry = () => {
    setTimeout(() => {
        if (hasError.value && retryCount.value < props.maxRetries) {
            handleRetry();
        }
    }, props.retryDelay);
};

const reset = () => {
    hasError.value = false;
    error.value = null;
    errorInfo.value = null;
    retryCount.value = 0;
    isRetrying.value = false;
};

// Watch for prop changes to reset state
watch(() => props.fallbackType, () => {
    if (!hasError.value) {
        reset();
    }
});

// Expose methods for parent components
defineExpose({
    reset,
    handleError,
    hasError: hasError.value,
    errorType: errorType.value
});
</script>

<style scoped>
.error-boundary {
    width: 100%;
    height: 100%;
}

.error-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    padding: 20px;
}

.error-boundary-content {
    width: 100%;
    height: 100%;
}

/* Error state animations */
.error-container {
    animation: errorSlideIn 0.3s ease-out;
}

@keyframes errorSlideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Loading state for retry */
.error-boundary:has(.error-container) {
    position: relative;
}

.error-boundary:has(.error-container)::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(2px);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
    z-index: 10;
}

.error-boundary.retrying::before {
    opacity: 1;
    pointer-events: all;
}

/* Dark mode support */
.dark .error-boundary:has(.error-container)::before {
    background: rgba(0, 0, 0, 0.8);
}

/* High contrast mode */
@media (prefers-contrast: high) {
    .error-container {
        border: 2px solid var(--n-error-color);
        border-radius: 8px;
    }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
    .error-container {
        animation: none;
    }

    .error-boundary:has(.error-container)::before {
        transition: none;
    }
}
</style>